// src/components/admin/PlansCreate.tsx
import React from "react";
import { useParams, useNavigate } from "react-router-dom";
import { http } from "../../lib/http";

// CRA / Vite 両対応の API ベース
const API_BASE: string =
  (process.env.REACT_APP_API_BASE as string) ||
  ((import.meta as any)?.env?.VITE_API_BASE as string) ||
  "http://localhost:4000";

const API_PREFIX = `${API_BASE.replace(/\/$/, "")}/api`;

// サーバが返してくるかもしれない画像URLのキー候補
const IMG_URL_KEYS = [
  "imageUrl",
  "image",
  "image_path",
  "imagePath",
  "thumbnailUrl",
  "thumbnail",
  "thumb",
  "photo",
  "picture",
  "img",
  "planImageUrl",
  "plan_image_url",
  "plan_image",
];

type PlanForm = {
  name: string;
  description: string;
  date: string;
  point: number | string;

  publishTimeLocal: string;
  raceTimes: string[];

  buyPt: string;
  grantPt: string;
  grantUsable: boolean;

  targetAmount: number | string;
  supplyCount: number | string;

  stakeHint: string; // ★ 推奨賭け金
  benefits: string;
  detailsText: string;

  imageUrl?: string;
  imageFile?: File | null;
  imagePreview?: string | null;

  // ★ 追加：表示期間（UI用）
  startAtLocal: string;
  endAtLocal: string;
};

const EMPTY: PlanForm = {
  name: "",
  description: "",
  date: "",
  point: "",

  publishTimeLocal: "",
  raceTimes: [],

  buyPt: "",
  grantPt: "",
  grantUsable: false,

  targetAmount: "",
  supplyCount: "",

  stakeHint: "",
  benefits: "",
  detailsText: "",

  imageUrl: "",
  imageFile: null,
  imagePreview: null,

  // ★ 初期値
  startAtLocal: "",
  endAtLocal: "",
};

function pickFirst(obj: any, keys: string[]): string {
  for (const k of keys) {
    const v = obj?.[k];
    if (typeof v === "string" && v.trim()) return v;
  }
  return "";
}

// datetime-local -> ISO（タイムゾーン付）
function toISOFromLocal(dtLocal: string): string {
  if (!dtLocal) return "";
  const d = new Date(dtLocal);
  return d.toISOString();
}

// ISO文字列 -> datetime-local 用（YYYY-MM-DDTHH:mm）
function toLocalInputString(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  const hh = String(d.getHours()).padStart(2, "0");
  const mi = String(d.getMinutes()).padStart(2, "0");
  return `${yyyy}-${mm}-${dd}T${hh}:${mi}`;
}

export default function PlansCreate() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();

  const [form, setForm] = React.useState<PlanForm>(EMPTY);
  const [loading, setLoading] = React.useState<boolean>(isEdit);
  const [saving, setSaving] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);

  // ===== 編集ロード =====
  React.useEffect(() => {
    if (!isEdit) {
      setForm(EMPTY);
      setLoading(false);
      return;
    }
    (async () => {
      try {
        setLoading(true);
        const data = await http.get<any>(`/admin/plans/${id}`);
        const imgUrl = pickFirst(data, IMG_URL_KEYS);

        // 既存：公開時間
        const publishTimeLocal = toLocalInputString(data.publishTime);

        // ★ 追加：表示開始/終了（startAt / endAt）
        const startAtLocal = toLocalInputString(data.startAt);
        const endAtLocal = toLocalInputString(data.endAt);

        setForm((prev) => ({
          ...prev,
          name: data.name ?? "",
          description: data.description ?? "",
          date: data.date ?? "",
          point: data.point ?? "",

          publishTimeLocal,
          raceTimes: Array.isArray(data.raceTimes) ? data.raceTimes : [],

          buyPt: data.buyPt ?? "",
          grantPt: data.grantPt ?? "",
          grantUsable: !!data.grantUsable,

          targetAmount: data.targetAmount ?? "",
          supplyCount: data.supplyCount ?? "",

          stakeHint: data.stakeHint ?? "",
          benefits: data.benefits ?? "",
          detailsText: data.detailsText ?? "",

          imageUrl: imgUrl,
          imageFile: null,
          imagePreview: imgUrl || null,

          // ★ 追加
          startAtLocal,
          endAtLocal,
        }));
      } catch {
        setErr("プランの読み込みに失敗しました");
      } finally {
        setLoading(false);
      }
    })();
  }, [isEdit, id]);

  // ===== 入力 =====
  const onChange: React.ChangeEventHandler<
    HTMLInputElement | HTMLTextAreaElement
  > = (e) => {
    const el = e.currentTarget;
    const name = el.name;
    let value: string | number | boolean =
      el.type === "checkbox" ? (el as HTMLInputElement).checked : el.value;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (name === "imageUrl") {
      setForm((prev) => ({
        ...prev,
        imagePreview: String(value),
        imageFile: null,
      }));
    }
  };

  const onPickFile: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const f = e.currentTarget.files?.[0] || null;
    if (!f) {
      setForm((p) => ({
        ...p,
        imageFile: null,
        imagePreview: p.imageUrl || null,
      }));
      return;
    }
    const url = URL.createObjectURL(f);
    setForm((p) => ({ ...p, imageFile: f, imagePreview: url }));
  };

  // ===== 配列項目 =====
  const addRaceTime = () =>
    setForm((p) => ({ ...p, raceTimes: [...(p.raceTimes || []), ""] }));
  const removeRaceTime = (idx: number) =>
    setForm((p) => ({
      ...p,
      raceTimes: p.raceTimes.filter((_, i) => i !== idx),
    }));
  const changeRaceTime = (idx: number, val: string) =>
    setForm((p) => {
      const next = [...p.raceTimes];
      next[idx] = val;
      return { ...p, raceTimes: next };
    });

  // ===== 送信 =====
  const onSubmit: React.FormEventHandler = async (e) => {
    e.preventDefault();
    setErr(null);

    if (!String(form.name).trim())
      return setErr("プラン名を入力してください");
    if (form.point === "" || Number.isNaN(Number(form.point)))
      return setErr("販売ポイントを数値で入力してください");
    if (!form.publishTimeLocal)
      return setErr("情報公開時間を入力してください");

    try {
      setSaving(true);

      const fd = new FormData();
      fd.append("name", String(form.name).trim());
      fd.append("description", form.description ?? "");
      fd.append("date", form.date || new Date().toISOString().slice(0, 10));
      fd.append("point", String(Number(form.point)));

      if (form.publishTimeLocal) {
        fd.append("publishTime", toISOFromLocal(form.publishTimeLocal));
      }

      // ★ 追加：startAt / endAt を ISO で送信（任意）
      if (form.startAtLocal) {
        fd.append("startAt", toISOFromLocal(form.startAtLocal));
      }
      if (form.endAtLocal) {
        fd.append("endAt", toISOFromLocal(form.endAtLocal));
      }

      if (form.raceTimes?.length) {
        fd.append(
          "raceTimes",
          JSON.stringify(form.raceTimes.filter(Boolean))
        );
      }

      fd.append("buyPt", form.buyPt ?? "");
      fd.append("grantPt", form.grantPt ?? "");
      fd.append("grantUsable", String(!!form.grantUsable));

      fd.append(
        "targetAmount",
        form.targetAmount === "" || form.targetAmount === undefined
          ? "0"
          : String(Number(form.targetAmount))
      );
      fd.append(
        "supplyCount",
        form.supplyCount === "" || form.supplyCount === undefined
          ? "0"
          : String(Number(form.supplyCount))
      );

      fd.append("stakeHint", form.stakeHint ?? "");
      fd.append("benefits", form.benefits ?? "");
      fd.append("detailsText", form.detailsText ?? "");

      if (form.imageUrl && form.imageUrl.trim() !== "") {
        fd.append("imageUrl", form.imageUrl.trim());
      }

      if (form.imageFile) {
        fd.append("image", form.imageFile);
      }

      let url = `${API_PREFIX}/admin/plans`;
      let method: "POST" | "PUT" = "POST";
      if (isEdit) {
        url = `${API_PREFIX}/admin/plans/${id}`;
        method = "PUT";
      }

      let res = await fetch(url, { method, body: fd, credentials: "include" });
      if (!res.ok && isEdit && res.status === 405) {
        fd.append("_method", "PUT");
        res = await fetch(`${API_PREFIX}/admin/plans/${id}`, {
          method: "POST",
          body: fd,
          credentials: "include",
        });
      }
      if (!res.ok) {
        const text = await res.text().catch(() => "");
        throw new Error(
          `保存に失敗しました（${res.status}）${text ? " / " + text : ""}`
        );
      }

      navigate("/admin/plans", { replace: true });
    } catch (e: any) {
      console.error(e);
      setErr(e?.message || "保存に失敗しました");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div style={{ padding: 16 }}>読み込み中...</div>;

  return (
    <div style={{ maxWidth: 1100, margin: "24px auto" }}>
      <h2>{isEdit ? `プラン編集 #${id}` : "プラン作成"}</h2>

      <form onSubmit={onSubmit}>
        {/* 基本情報 */}
        <section
          style={{
            background: "#fff",
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <div style={{ marginBottom: 12 }}>
            <label>プラン名</label>
            <input
              name="name"
              value={form.name}
              onChange={onChange}
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ marginBottom: 12 }}>
            <label>説明</label>
            <textarea
              name="description"
              value={form.description}
              onChange={onChange}
              rows={3}
              style={{ width: "100%" }}
            />
          </div>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div>
              <label>開催日</label>
              <input
                type="date"
                name="date"
                value={form.date}
                onChange={onChange}
              />
            </div>
            <div>
              <label>販売ポイント</label>
              <input
                type="number"
                name="point"
                value={form.point}
                onChange={onChange}
              />
            </div>
          </div>
        </section>

        {/* 公開情報 */}
        <section
          style={{
            background: "#fff",
            padding: 16,
            borderRadius: 8,
            marginBottom: 16,
          }}
        >
          <h3 style={{ marginTop: 0 }}>公開情報</h3>

          <div style={{ marginBottom: 12 }}>
            <label>情報公開時間</label>
            <input
              type="datetime-local"
              name="publishTimeLocal"
              value={form.publishTimeLocal}
              onChange={onChange}
              style={{ width: "100%" }}
            />
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              指定時刻になると、
              <strong>購入者のマイページ</strong>
              でこのプランの情報が閲覧可能になります。
            </div>
          </div>

          {/* ★ 追加：販売開始・販売終了（表示期間） */}
          <div style={{ marginBottom: 12 }}>
            <label>販売開始日時（表示開始）</label>
            <input
              type="datetime-local"
              name="startAtLocal"
              value={form.startAtLocal}
              onChange={onChange}
              style={{ width: "100%" }}
            />
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              この日時以降に、ユーザー画面のプラン一覧に表示されます（未入力の場合は制限なし）。
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label>販売終了日時（表示終了）</label>
            <input
              type="datetime-local"
              name="endAtLocal"
              value={form.endAtLocal}
              onChange={onChange}
              style={{ width: "100%" }}
            />
            <div style={{ fontSize: 12, color: "#64748b", marginTop: 4 }}>
              この日時以降は、ユーザー画面のプラン一覧から非表示になります（未入力の場合は制限なし）。
            </div>
          </div>

          <div style={{ marginBottom: 8 }}>発走時刻（複数可）</div>
          {form.raceTimes.map((t, i) => (
            <div
              key={i}
              style={{ display: "flex", gap: 8, marginBottom: 8 }}
            >
              <input
                value={t}
                onChange={(e) => changeRaceTime(i, e.target.value)}
                placeholder="例：14:20"
                style={{ flex: 1 }}
              />
              <button type="button" onClick={() => removeRaceTime(i)}>
                − 削除
              </button>
            </div>
          ))}
          <button
            type="button"
            onClick={addRaceTime}
            style={{ marginBottom: 12 }}
          >
            ＋ 追加
          </button>

          <div
            style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}
          >
            <div>
              <label>購入PT</label>
              <input name="buyPt" value={form.buyPt} onChange={onChange} />
              <div style={{ marginTop: 6 }}>
                <label>
                  <input
                    type="checkbox"
                    name="grantUsable"
                    checked={!!form.grantUsable}
                    onChange={onChange}
                  />{" "}
                  付与PTをプラン購入に使える
                </label>
              </div>
            </div>
            <div>
              <label>付与PT</label>
              <input
                name="grantPt"
                value={form.grantPt}
                onChange={onChange}
              />
            </div>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "1fr 1fr",
              gap: 16,
              marginTop: 12,
            }}
          >
            <div>
              <label>目標金額</label>
              <input
                name="targetAmount"
                value={form.targetAmount ?? ""}
                onChange={onChange}
              />
            </div>
            <div>
              <label>提供数</label>
              <input
                name="supplyCount"
                value={form.supplyCount ?? ""}
                onChange={onChange}
              />
            </div>
          </div>

          {/* ★ 推奨賭け金 */}
          <div style={{ marginTop: 12 }}>
            <label>推奨賭け金</label>
            <input
              name="stakeHint"
              value={form.stakeHint}
              onChange={onChange}
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <label>購入特典</label>
            <input
              name="benefits"
              value={form.benefits}
              onChange={onChange}
              style={{ width: "100%" }}
            />
          </div>

          <div style={{ marginTop: 12 }}>
            <label>買い目・詳細情報</label>
            <textarea
              name="detailsText"
              value={form.detailsText}
              onChange={onChange}
              rows={10} // 行数を増やして入力しやすく
              style={{
                width: "100%",
                minHeight: 220, // 画面幅に関係なく十分な縦幅を確保
                boxSizing: "border-box",
              }}
            />
          </div>


          {/* プラン画像 */}
          <div style={{ marginTop: 12 }}>
            <label
              style={{ fontWeight: 700, display: "block", marginBottom: 4 }}
            >
              プラン画像
            </label>
            <input type="file" accept="image/*" onChange={onPickFile} />
            <div style={{ color: "#666", fontSize: 12, marginTop: 4 }}>
              ファイルを選ぶとそれが優先されます。未選択時は下のURLを使用します。
            </div>

            <div style={{ marginTop: 10 }}>
              <label style={{ display: "block" }}>
                プラン画像URL（任意）
              </label>
              <input
                name="imageUrl"
                value={form.imageUrl || ""}
                onChange={onChange}
                placeholder="https://example.com/plan.webp"
                style={{ width: "100%" }}
              />
            </div>

            {form.imagePreview && (
              <div style={{ marginTop: 8 }}>
                <img
                  src={form.imagePreview}
                  alt="プレビュー"
                  style={{
                    maxWidth: 320,
                    border: "1px solid #ddd",
                    borderRadius: 8,
                    display: "block",
                  }}
                />
              </div>
            )}
          </div>
        </section>

        {err && <div style={{ color: "crimson", marginBottom: 12 }}>{err}</div>}

        <div>
          <button
            type="submit"
            disabled={saving}
            style={{
              background: "#1976d2",
              color: "#fff",
              border: "none",
              padding: "8px 16px",
              borderRadius: 4,
            }}
          >
            {saving ? "保存中..." : "保存する"}
          </button>
          <button
            type="button"
            onClick={() => navigate("/admin/plans")}
            style={{
              marginLeft: 8,
              padding: "8px 16px",
              border: "1px solid #999",
              borderRadius: 4,
            }}
          >
            戻る
          </button>
        </div>
      </form>
    </div>
  );
}
